import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  CheckCircle2, RotateCcw, Clock, AlertCircle, Loader2, Palette, Plus, Paperclip,
} from 'lucide-react';
import { formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ImageLightbox from '../components/DesignImageLightbox';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TYPES = ['CAM Piece', 'Mould', 'CAD', 'Render', 'Other'];
const MAX_FILES = 10;
const MAX_BYTES = 5 * 1024 * 1024;

const imgSrc = (depId, img) => {
  if (img.url) return img.url;
  const token = localStorage.getItem('siddhi_token') || '';
  return `${API}/design-dependencies/${depId}/images/${img.id}?token=${encodeURIComponent(token)}`;
};

const STATUS_META = {
  pending: { label: 'Pending', icon: Clock, bg: 'bg-orange-50 dark:bg-orange-500/10', border: 'border-orange-300/60 dark:border-orange-500/30', text: 'text-orange-700 dark:text-orange-300' },
  overdue: { label: 'Overdue', icon: AlertCircle, bg: 'bg-rose-50 dark:bg-rose-500/10', border: 'border-rose-300/60 dark:border-rose-500/30', text: 'text-rose-700 dark:text-rose-300' },
  completed_on_time: { label: 'Completed On Time', icon: CheckCircle2, bg: 'bg-emerald-50 dark:bg-emerald-500/10', border: 'border-emerald-300/60 dark:border-emerald-500/30', text: 'text-emerald-700 dark:text-emerald-300' },
  completed_late: { label: 'Completed Late', icon: CheckCircle2, bg: 'bg-amber-50 dark:bg-amber-500/10', border: 'border-amber-300/60 dark:border-amber-500/30', text: 'text-amber-700 dark:text-amber-300' },
};

const isCompleted = (s) => s === 'completed_on_time' || s === 'completed_late';

const validateFiles = (existingCount, files) => {
  const list = Array.from(files || []);
  if (list.length === 0) return [];
  if (existingCount + list.length > MAX_FILES) { toast.error(`Max ${MAX_FILES} images`); return []; }
  for (const f of list) {
    if (!f.type.startsWith('image/')) { toast.error(`${f.name}: not an image`); return []; }
    if (f.size > MAX_BYTES) { toast.error(`${f.name}: max 5 MB`); return []; }
  }
  return list;
};

const uploadAll = async (url, files) => {
  for (const f of files) {
    const fd = new FormData();
    fd.append('file', f);
    await axios.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

// ── Create Request dialog (production / admin only) ─────────────────────────
const CreateRequestDialog = ({ open, onOpenChange, orders, designers, customers, onCreated }) => {
  const [orderId, setOrderId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [type, setType] = useState('CAD');
  const [styleNumber, setStyleNumber] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const standalone = !orderId;

  const reset = () => {
    setOrderId(''); setCustomerName(''); setType('CAD'); setStyleNumber('');
    setDueDate(''); setAssignedTo(''); setNotes(''); setFiles([]);
  };

  const submit = async () => {
    if (!styleNumber.trim()) return toast.error('Style number is required');
    if (!dueDate) return toast.error('Due date is required');
    if (!assignedTo) return toast.error('Pick a designer');
    if (standalone && !customerName) return toast.error('Pick a customer (or pick an order)');
    setSaving(true);
    try {
      const body = {
        dependency_type: type,
        style_number: styleNumber.trim(),
        due_date: dueDate,
        assigned_to_user_id: assignedTo,
        request_notes: notes.trim() || null,
      };
      if (orderId) body.order_id = orderId;
      else body.customer_name = customerName;
      const { data: created } = await axios.post(`${API}/design-dependencies`, body);
      if (files.length > 0) {
        await uploadAll(`${API}/design-dependencies/${created.id}/images/request`, files);
      }
      toast.success('Request created');
      reset();
      onOpenChange(false);
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="create-request-dialog">
        <DialogHeader>
          <DialogTitle>Create Design Request</DialogTitle>
          <DialogDescription>
            Link to an order, or pick a KYC-approved customer for a standalone request.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Order (optional)</Label>
            <Select value={orderId || 'standalone'} onValueChange={(v) => setOrderId(v === 'standalone' ? '' : v)}>
              <SelectTrigger data-testid="cr-order-select">
                <SelectValue placeholder="Pick an order or leave standalone…" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectItem value="standalone">— Standalone (no order) —</SelectItem>
                {orders.map(o => (
                  <SelectItem key={o.id} value={o.id}>{o.order_number} · {o.customer_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {standalone && (
            <div data-testid="cr-customer-block">
              <Label className="text-xs">Customer (KYC-approved)</Label>
              <Select value={customerName} onValueChange={setCustomerName}>
                <SelectTrigger data-testid="cr-customer-select">
                  <SelectValue placeholder={customers.length ? 'Pick a customer…' : 'No KYC-approved customers'} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {customers.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {customers.length === 0 && (
                <p className="text-[10px] text-amber-600 mt-1">No KYC-approved customers yet. Approve KYC from Customers page.</p>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="cr-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Style Number</Label>
            <Input value={styleNumber} onChange={(e) => setStyleNumber(e.target.value)} placeholder="e.g. ST-1050" data-testid="cr-style-input" />
          </div>
          <div>
            <Label className="text-xs">Due Date</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="cr-due-input" />
          </div>
          <div>
            <Label className="text-xs">Assigned To</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger data-testid="cr-assignee-select"><SelectValue placeholder={designers.length ? 'Pick a designer' : 'No designers found'} /></SelectTrigger>
              <SelectContent>
                {designers.map(d => <SelectItem key={d.id} value={d.id}>{d.full_name || d.name || d.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Request Notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Need 3 angle renders, gold tone, plain background" data-testid="cr-notes-input" />
          </div>
          <div>
            <Label className="text-xs">Reference Images (optional, up to 10)</Label>
            <Input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => setFiles(validateFiles(0, e.target.files))}
              data-testid="cr-files-input"
            />
            {files.length > 0 && <p className="text-[10px] text-muted-foreground mt-1">{files.length} file(s) selected</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="cr-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TaskRow = ({ row, busyId, onComplete, onReopen, onAttach }) => {
  const meta = STATUS_META[row.status] || STATUS_META.pending;
  const Icon = meta.icon;
  const done = isCompleted(row.status);
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState({ side: null, idx: null });
  const replyRef = React.useRef(null);

  const reqImgs = row.request_images || [];
  const replyImgs = row.reply_images || [];

  return (
    <div
      className={cn('rounded-lg border p-3 space-y-2', meta.bg, meta.border)}
      data-testid={`mytask-row-${row.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          {row.order_id ? (
            <button
              type="button"
              onClick={() => navigate(`/orders/${row.order_id}`)}
              className="text-sm font-bold underline-offset-2 hover:underline"
              data-testid={`mytask-order-${row.id}`}
            >
              {row.order_number}
            </button>
          ) : (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 mr-1" data-testid={`mytask-standalone-${row.id}`}>STANDALONE</Badge>
          )}
          <span className="text-xs text-muted-foreground"> · {row.customer_name || ''}</span>
          <p className="text-xs text-foreground mt-1">
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 mr-1.5">{row.dependency_type}</Badge>
            <span className="font-mono font-semibold">{row.style_number}</span>
            <span className="text-muted-foreground"> · Due {formatDate(row.due_date)}</span>
            {row.assigned_to_name && (
              <span className="text-muted-foreground"> · @ {row.assigned_to_name}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={cn('flex items-center gap-1 text-xs font-bold', meta.text)}>
            <Icon className="h-3.5 w-3.5" />
            {meta.label}
          </div>
        </div>
      </div>

      {row.request_notes && (
        <p className="text-xs italic bg-background/50 px-2 py-1 rounded">“{row.request_notes}”
          {row.created_by_name ? <span className="text-muted-foreground"> — {row.created_by_name}</span> : null}
        </p>
      )}
      {reqImgs.length > 0 && (
        <div>
          <p className="text-[10px] uppercase font-semibold text-muted-foreground">References ({reqImgs.length})</p>
          <div className="flex flex-wrap gap-1 mt-1">
            {reqImgs.slice(0, 3).map((img, i) => (
              <button
                key={img.id}
                type="button"
                onClick={() => setLightbox({ side: 'request', idx: i })}
                className="h-14 w-14 rounded overflow-hidden border bg-muted hover:ring-2 hover:ring-violet-400"
                data-testid={`mytask-req-thumb-${img.id}`}
              >
                <img src={imgSrc(row.id, img)} alt={img.name} className="h-full w-full object-cover" />
              </button>
            ))}
            {reqImgs.length > 3 && (
              <button type="button" onClick={() => setLightbox({ side: 'request', idx: 3 })} className="h-14 w-14 rounded border text-xs font-bold bg-muted hover:bg-muted/80">
                +{reqImgs.length - 3}
              </button>
            )}
          </div>
        </div>
      )}
      {(row.reply_notes || replyImgs.length > 0) && (
        <div className="border-t pt-2">
          {row.reply_notes && <p className="text-xs italic text-emerald-900 dark:text-emerald-100">↪ “{row.reply_notes}”</p>}
          {replyImgs.length > 0 && (
            <>
              <p className="text-[10px] uppercase font-semibold text-muted-foreground mt-1">Reply ({replyImgs.length})</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {replyImgs.slice(0, 3).map((img, i) => (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setLightbox({ side: 'reply', idx: i })}
                    className="h-14 w-14 rounded overflow-hidden border bg-muted hover:ring-2 hover:ring-emerald-400"
                    data-testid={`mytask-reply-thumb-${img.id}`}
                  >
                    <img src={imgSrc(row.id, img)} alt={img.name} className="h-full w-full object-cover" />
                  </button>
                ))}
                {replyImgs.length > 3 && (
                  <button type="button" onClick={() => setLightbox({ side: 'reply', idx: 3 })} className="h-14 w-14 rounded border text-xs font-bold bg-muted hover:bg-muted/80">
                    +{replyImgs.length - 3}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={replyRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const list = validateFiles(replyImgs.length, e.target.files);
            if (list.length === 0) { e.target.value = ''; return; }
            onAttach(row.id, 'reply', list).then(() => { e.target.value = ''; });
          }}
        />
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          onClick={() => replyRef.current?.click()}
          data-testid={`mytask-attach-${row.id}`}
        >
          <Paperclip className="h-3.5 w-3.5 mr-1" /> Attach reply
        </Button>
        {!done ? (
          <Button
            size="sm"
            className="h-8 bg-emerald-600 hover:bg-emerald-700"
            disabled={busyId === row.id}
            onClick={() => onComplete(row.id)}
            data-testid={`mytask-complete-${row.id}`}
          >
            {busyId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark Complete</>}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            disabled={busyId === row.id}
            onClick={() => onReopen(row.id)}
            data-testid={`mytask-reopen-${row.id}`}
          >
            {busyId === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reopen</>}
          </Button>
        )}
      </div>

      {lightbox.idx != null && (
        <ImageLightbox
          depId={row.id}
          images={lightbox.side === 'request' ? reqImgs : replyImgs}
          openIndex={lightbox.idx}
          onClose={() => setLightbox({ side: null, idx: null })}
        />
      )}
    </div>
  );
};

const MyDesignTasks = () => {
  const { user } = useAuth();
  const canCreate = ['admin', 'super_admin', 'production', 'designer'].includes(user?.role);
  const elevated = ['admin', 'super_admin', 'production'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, overdue: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [customers, setCustomers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/design-dependencies/my`, {
        params: { include_completed: includeCompleted, mine_only: mineOnly },
      });
      setRows(r.data.dependencies);
      setCounts(r.data.counts);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load tasks');
    } finally { setLoading(false); }
  }, [includeCompleted, mineOnly]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!canCreate) return;
    axios.get(`${API}/orders`).then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data.orders || []);
      setOrders(list
        .filter(o => o.status !== 'Dispatched' && !o.archived)
        .map(o => ({ id: o.id, order_number: o.order_number || o.co_number, customer_name: o.customer_name }))
        .slice(0, 500)
      );
    }).catch(() => {});
    axios.get(`${API}/users`).then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data.users || []);
      setDesigners(list.filter(u => u.role === 'designer' && u.is_active !== false));
    }).catch(() => {});
    axios.get(`${API}/design-dependencies/customers`).then(r => {
      setCustomers(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setCustomers([]));
  }, [canCreate]);

  const handleComplete = async (id) => {
    setBusyId(id);
    try { await axios.post(`${API}/design-dependencies/${id}/complete`, {}); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusyId(null); }
  };
  const handleReopen = async (id) => {
    setBusyId(id);
    try { await axios.post(`${API}/design-dependencies/${id}/reopen`); await load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setBusyId(null); }
  };
  const handleAttach = async (depId, side, files) => {
    setBusyId(depId);
    try {
      await uploadAll(`${API}/design-dependencies/${depId}/images/${side}`, files);
      await load();
      toast.success(`Added ${files.length} image(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Palette className="h-6 w-6 text-violet-500" /> Design Tasks
            </h1>
            <p className="text-xs text-muted-foreground">
              {user?.role === 'designer'
                ? 'Design tasks assigned to you.'
                : (mineOnly ? 'Your assigned design tasks.' : 'All design tasks across the team.')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {elevated && (
              <div className="flex items-center gap-2">
                <Switch id="mine-only" checked={mineOnly} onCheckedChange={setMineOnly} data-testid="toggle-mine-only" />
                <Label htmlFor="mine-only" className="text-xs">Only mine</Label>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Switch id="include-completed" checked={includeCompleted} onCheckedChange={setIncludeCompleted} data-testid="toggle-include-completed" />
              <Label htmlFor="include-completed" className="text-xs">Show completed</Label>
            </div>
            {canCreate && (
              <Button onClick={() => setCreateOpen(true)} className="bg-violet-600 hover:bg-violet-700" data-testid="create-request-btn">
                <Plus className="h-4 w-4 mr-1" /> Create Request
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card data-testid="kpi-total"><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{counts.total}</p><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Total</p></CardContent></Card>
          <Card data-testid="kpi-pending"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{counts.pending}</p><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Pending</p></CardContent></Card>
          <Card data-testid="kpi-overdue"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{counts.overdue}</p><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Overdue</p></CardContent></Card>
          <Card data-testid="kpi-completed"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{counts.completed}</p><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Completed</p></CardContent></Card>
        </div>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Tasks</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {loading ? <Skeleton className="h-32 w-full" />
              : rows.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10">
                  {includeCompleted ? 'No tasks assigned to you.' : 'No open tasks.'}
                </p>
              ) : rows.map(r => (
                <TaskRow key={r.id} row={r} busyId={busyId} onComplete={handleComplete} onReopen={handleReopen} onAttach={handleAttach} />
              ))}
          </CardContent>
        </Card>

        {canCreate && (
          <CreateRequestDialog
            open={createOpen}
            onOpenChange={setCreateOpen}
            orders={orders}
            designers={designers}
            customers={customers}
            onCreated={load}
          />
        )}
      </div>
    </div>
  );
};

export default MyDesignTasks;
